/**
 * Copyright © 2025-2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import "./services/firebase";

import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { colors } from "@farmlands/mui-theme";
import { ShadcnThemeProvider } from "@farmlands/shadcn-ui";
import { Box, ThemeProvider } from "@mui/material";
import { useEffect } from "react";
import { Provider, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { PersistGate } from "redux-persist/integration/react";

import packageJson from "../package.json";

import { Loader } from "./components/Loader";
import { selectCurrentAccountNumber } from "./features/azure-b2c/storage";
import apiClient from "./services/api-client";
import { setFarmlandsProApiAccountNumber } from "./services/farmlandspro-api";
import { selectRemoteConfig } from "./services/firebase/remote-config/remoteConfig.slice";
import store, { persistor } from "./store/rootStore";
import { farmlandsMuiTheme } from "./theme/farmlands-mui-theme";
import { AppBrowserRouter } from "./AppBrowserRouter";

import "react-toastify/dist/ReactToastify.css";

const VersionCheck = () => {
  const remoteConfig = useSelector(selectRemoteConfig);
  const uiVersion = packageJson.version;

  const reloadPage = () => {
    persistor.pause();
    window.location.reload();
  };

  useEffect(() => {
    if (uiVersion !== remoteConfig.latestVersion) {
      toast.onChange((e) => {
        if (e.id === "reload" && e.status === "removed") {
          reloadPage();
        }
      });
      toast("Reload for latest version", {
        autoClose: false,
        toastId: "reload",
        closeButton: () => (
          // Must be manually styled as it sits outside of MuiTheme context.
          <button onClick={reloadPage} className="toast-refresh-button">
            Refresh
          </button>
        ),
      });
    }
  }, [uiVersion, remoteConfig.latestVersion]);

  return null;
};

const syncApiAccountNumberFromStore = () => {
  const accountNumber = selectCurrentAccountNumber(store.getState());
  if (accountNumber) {
    apiClient.setAccountNumber(accountNumber);
    setFarmlandsProApiAccountNumber(accountNumber);
  }
};

// Hide the splash (you should do this on app launch)
function App() {
  if (Capacitor.isNativePlatform()) {
    SplashScreen.hide();
  }

  return (
    <ShadcnThemeProvider theme="verdure">
      <ThemeProvider theme={farmlandsMuiTheme()}>
        <Provider store={store}>
          <PersistGate
            persistor={persistor}
            onBeforeLift={syncApiAccountNumberFromStore}
            loading={
              Capacitor.isNativePlatform() ? (
                <Box width="100%" height="100%" bgcolor={colors.primary700}>
                  <Loader title="Launching" />
                </Box>
              ) : (
                <Loader title="Launching" />
              )
            }
          >
            {!Capacitor.isNativePlatform() && <VersionCheck />}
            <AppBrowserRouter />
          </PersistGate>
        </Provider>
      </ThemeProvider>
    </ShadcnThemeProvider>
  );
}

export default App;
